import { Dropdown, Pivot, PivotItem, Stack, StackItem } from '@fluentui/react';
import React from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { buildGlobalSettingsPatch, IGlobalStorefrontSettings, PAYMENT_PROCESSOR_KEYS } from '../../../../models/Storefront';
import { useGetTaxCodesQuery } from '../../../../store/Api';
import { useGetOrganizationFeatureSettingsQuery, useUpdateOrganizationFeatureSettingsMutation } from '../../../../store/endpoints/OrganizationsEndpoints';
import { useGetGlobalStorefrontSettingsQuery, usePatchGlobalStorefrontSettingsMutation } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import OrderEmailTemplateField from '../../../../components/Storefront/OrderEmailTemplateField';
import { RootState } from '../../../../store/rootReducer';
import DataField from '../../../../components/Common/DataField';
import RichTextField from '../../../../components/Common/RichTextField';
import ToggleField from '../../../../components/Common/ToggleField';
import StorefrontGlobalQuestionsForm from '../../../../components/Storefront/StorefrontGlobalQuestionsForm';
import PaymentProcessorDetails from '../../../../components/Storefront/PaymentProcessors/PaymentProcessorDetails';
import CurrencySelection from '../../../../components/CurrencySelection/CurrencySelection';
import PaymentProcessorDropdown from '../../../../components/Storefront/PaymentProcessorDropdown';
import NumberField from '../../../../components/Inputs/NumberField';
import { getCurrencySymbol } from '../../../../logic/currencyLocalizationHelper';
import ShippingChargeSelection from '../../../../components/Storefront/ShippingChargeSelection';
import Setting from '../../../../components/Common/Setting';
import ConditionalSetting from '../../../../components/Common/ConditionalSetting';
import { useUrlTabState } from '../../../../hooks/useUrlState';

/** Returns only the scalar fields that differ between original and current. */

const DEBOUNCE_MS = 3000;

const StorefrontGlobalOrderingAndCartsSettings: React.FC = () => {
    const globalSettingsQuery = useGetGlobalStorefrontSettingsQuery();
    // Seed the editor only; nothing is written until an edit is made. Up here with the other hooks
    const [patchGlobalSettings] = usePatchGlobalStorefrontSettingsMutation();
    // In the URL so a documentation link can land on the tab holding the setting it describes.
    const [selectedTab, setSelectedTab] = useUrlTabState('email');

    const [localSettings, setLocalSettingsRaw] = React.useState<IGlobalStorefrontSettings>();
    // Ref mirrors state so callbacks always access the latest value without stale closures
    const localSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    // Tracks the last version successfully persisted to the server
    const originalSettingsRef = React.useRef<IGlobalStorefrontSettings>();
    const debounceSaveRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const orgFeatures = useGetOrganizationFeatureSettingsQuery(selectedOrganization ?? '', { skip: !selectedOrganization || selectedOrganization === '' });
    const [updateOrganizationFeatureSettings] = useUpdateOrganizationFeatureSettingsMutation();
    const taxCodes = useGetTaxCodesQuery();

    const setLocalSettings = React.useCallback((s: IGlobalStorefrontSettings) => {
        localSettingsRef.current = s;
        setLocalSettingsRaw(s);
    }, []);

    // Initialise local state once from the server; subsequent query refetches are ignored
    // so the user's in-progress edits are not overwritten.
    React.useEffect(() => {
        if (globalSettingsQuery.data && !originalSettingsRef.current) {
            setLocalSettings(globalSettingsQuery.data);
            originalSettingsRef.current = globalSettingsQuery.data;
        }
    }, [globalSettingsQuery.data, setLocalSettings]);

    // Flush any pending debounced save when the component unmounts
    React.useEffect(() => {
        return () => {
            if (debounceSaveRef.current) {
                clearTimeout(debounceSaveRef.current);
                if (localSettingsRef.current && originalSettingsRef.current) {
                    const patch = buildGlobalSettingsPatch(originalSettingsRef.current, localSettingsRef.current);
                    if (Object.keys(patch).length > 0) {
                        patchGlobalSettings(patch);
                    }
                }
            }
        };
    }, []);

    const doSave = React.useCallback(async (current: IGlobalStorefrontSettings) => {
        if (!originalSettingsRef.current) return;
        const patch = buildGlobalSettingsPatch(originalSettingsRef.current, current);
        if (Object.keys(patch).length === 0) return;
        try {
            await patchGlobalSettings(patch).unwrap();
            // Advance the baseline so future diffs are correct
            originalSettingsRef.current = { ...originalSettingsRef.current, ...patch };
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    }, [patchGlobalSettings]);

    /** Schedule a save after DEBOUNCE_MS. Resets the timer if called again before it fires. */
    const scheduleSave = React.useCallback(() => {
        if (debounceSaveRef.current) clearTimeout(debounceSaveRef.current);
        debounceSaveRef.current = setTimeout(() => {
            if (localSettingsRef.current) doSave(localSettingsRef.current);
        }, DEBOUNCE_MS);
    }, [doSave]);

    /** Cancel any pending debounced save and persist immediately. */
    const saveNow = React.useCallback(() => {
        if (debounceSaveRef.current) {
            clearTimeout(debounceSaveRef.current);
            debounceSaveRef.current = null;
        }
        if (localSettingsRef.current) doSave(localSettingsRef.current);
    }, [doSave]);

    if (globalSettingsQuery.isLoading || !localSettings) {
        return <div>Loading...</div>;
    }

    const handleValueChange = (field: keyof IGlobalStorefrontSettings, value?: string) => {
        const updated = { ...(localSettingsRef.current ?? localSettings), [field]: value };
        setLocalSettings(updated);
        doSave(updated);
    }

    const handleNumberChange = (field: keyof IGlobalStorefrontSettings, value?: number) => {
        const updated = { ...(localSettingsRef.current ?? localSettings), [field]: value };
        setLocalSettings(updated);
        doSave(updated);
    }

    const handleDataFieldChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string, name?: string) => {
        if (!name) return;
        setLocalSettings({ ...(localSettingsRef.current ?? localSettings), [name]: newValue ?? '' });
        scheduleSave();
    }

    const handleToggleChange = (event?: React.FormEvent<HTMLInputElement | HTMLElement>, checked?: boolean, name?: string) => {
        if (!name) return;
        setLocalSettings({ ...(localSettingsRef.current ?? localSettings), [name]: checked ?? false });
        scheduleSave();
    }

    const handleSimpleTaxCalculationChange = async (checked?: boolean) => {
        if (!selectedOrganization || !orgFeatures.data) return;
        try {
            await updateOrganizationFeatureSettings({
                id: selectedOrganization,
                request: { ...orgFeatures.data, enableSimpleTaxCalculation: checked ?? false },
            }).unwrap();
            toast.success('Settings saved');
        } catch {
            toast.error('Failed to save settings');
        }
    }


    return (
        <div>
            <h1>Storefront Ordering and Carts Settings</h1>
            <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey)}>
                <PivotItem headerText='Email Settings' itemKey='email'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <StackItem>
                            <DataField
                                settingId='storefront.global.ordering.orderEmail'
                                label='Order Confirmation BCC'
                                type='email'
                                value={localSettings.orderEmail ?? ''}
                                name='orderEmail'
                                onChange={handleDataFieldChange}
                                onBlur={saveNow}
                            />
                        </StackItem>
                        <StackItem>
                            <DataField
                                settingId='storefront.global.ordering.orderFailureEmail'
                                label='Order Failure Email'
                                type='email'
                                value={localSettings.orderFailureEmail ?? ''}
                                name='orderFailureEmail'
                                onChange={handleDataFieldChange}
                                onBlur={saveNow}
                                toolTip='Specify an email address to be notified if there is an error processing a customer order event'
                            />
                        </StackItem>
                        <StackItem>
                            <DataField
                                settingId='storefront.global.ordering.customerEmailSubject'
                                label='Order Confirmation Email Subject'
                                value={localSettings.customerEmailSubject ?? ''}
                                name='customerEmailSubject'
                                onChange={handleDataFieldChange}
                                onBlur={saveNow}
                                toolTip='Customize the subject line for order confirmation emails sent to customers. Supports tags. To view the complete list of supported tags, check our documentation'
                                toolTipLocation='right'
                            />
                        </StackItem>
                        {/* One field where there were three, matching the per-storefront Ordering
                            page and the global settings form. The toggle only chose between this
                            editor and a text box the stock template already renders through
                            {{{customer_email_text}}}, and the send path never read this global copy
                            of the flag anyway -- it reads the STOREFRONT's.

                            Customer Email Text is deprecated, not deleted: it is still the org-wide
                            fallback for every storefront that sets none of its own, so the setting
                            and its token stay. The editor opens on the stock template with that text
                            already substituted in, so saving carries it across. */}
                        <StackItem>
                            <OrderEmailTemplateField
                                label='Default Order Email Template'
                                value={localSettings.customEmailTemplate}
                                customerEmailText={localSettings.customerEmailText}
                                onChange={(value) => {
                                    const updated = {
                                        ...(localSettingsRef.current ?? localSettings),
                                        customEmailTemplate: value,
                                        // Kept in step with the template, as the other screens do.
                                        useCustomEmailTemplate: !!value.trim(),
                                    };
                                    setLocalSettings(updated);
                                    doSave(updated);
                                }}
                            />
                        </StackItem>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Order Questions' itemKey='questions'>
                    <StorefrontGlobalQuestionsForm globalSettings={localSettings} />
                </PivotItem>
                <PivotItem headerText='Terms and Conditions' itemKey='terms'>
                    <Stack tokens={{ childrenGap: '1em' }}>
                        <RichTextField
                            label='Terms and Conditions'
                            value={localSettings.termsAndConditions}
                            onChange={(v) => handleValueChange('termsAndConditions', v)}
                        />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Payments & Billing' itemKey='payments'>
                    <h2>Payment Processor Setup</h2>
                    <PaymentProcessorDetails />
                    <PaymentProcessorDropdown
                        value={localSettings.paymentProcessor ?? PAYMENT_PROCESSOR_KEYS.NONE}
                        onChange={(v) => handleValueChange('paymentProcessor', v)}
                    />
                    <h2>Currency</h2>
                    <CurrencySelection
                        value={localSettings.currency}
                        onChange={(v) => handleValueChange('currency', v)}
                    />
                    <h2>Tax Settings</h2>
                    <ToggleField
                        settingId='storefront.global.ordering.enableSimpleTaxCalculation'
                        inline
                        label='Enable Simple Tax Calculation'
                        checked={orgFeatures.data?.enableSimpleTaxCalculation ?? false}
                        onChange={(_e, checked) => handleSimpleTaxCalculationChange(checked)}
                        disabled={!orgFeatures.data}
                    />
                    <ToggleField
                        settingId='storefront.global.ordering.collectTax'
                        inline
                        label='Collect Tax'
                        checked={localSettings.collectTax ?? false}
                        name='collectTax'
                        onChange={handleToggleChange}
                    />
                    <ConditionalSetting
                        when={!!(localSettings.collectTax && orgFeatures?.data?.enableSimpleTaxCalculation)}
                        id='storefront.global.ordering.defaultTaxCode'
                    >
                        <Setting id='storefront.global.ordering.defaultTaxCode'>
                            <Dropdown
                                label={'Default Tax Code'}
                                placeholder={'Not Set'}
                                selectedKey={localSettings.defaultTaxCode}
                                options={[...(taxCodes?.data?.map(taxCode => ({
                                    key: taxCode.name ?? '',
                                    text: `${taxCode.name ?? ''} (${taxCode.rate?.toString()}%)`
                                })) ?? [])]}
                                onChange={(e, v) => handleValueChange('defaultTaxCode', v?.key.toString())}
                            />
                        </Setting>
                    </ConditionalSetting>
                </PivotItem>
                <PivotItem headerText='Shipping' itemKey='shipping'>
                    <Setting id='storefront.global.ordering.defaultShippingCharge'>
                        <NumberField
                            prefix={getCurrencySymbol(localSettings.currency)}
                            label='Default Shipping Charge'
                            decimal={true}
                            value={localSettings.defaultShippingCharge}
                            name='defaultShippingCharge'
                            onChange={(v) => handleNumberChange('defaultShippingCharge', v)}
                        />
                    </Setting>
                    <ShippingChargeSelection
                        isLowestLevel={true}
                        value={localSettings.defaultShippingChargeType}
                        onChange={(v) => handleValueChange('defaultShippingChargeType', v)}
                        label='Default Shipping Charge Type'
                    />
                </PivotItem>
            </Pivot>
        </div>
    );
};

export default StorefrontGlobalOrderingAndCartsSettings;
